import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

interface PreviewDialogProps {
  open: boolean;
  onClose: () => void;
  fileUrl: string | string[];
  fileName?: string | string[];
  fileType?: ("image" | "video") | ("image" | "video")[];
  initialIndex?: number;
}

const PreviewDialog: React.FC<PreviewDialogProps> = ({
  open,
  onClose,
  fileUrl,
  fileName = "file",
  fileType = "image",
  initialIndex = 0,
}) => {
  const files = useMemo(() => (Array.isArray(fileUrl) ? fileUrl : [fileUrl]), [fileUrl]);
  const names = useMemo(() => (Array.isArray(fileName) ? fileName : [fileName]), [fileName]);
  const types = useMemo(() => (Array.isArray(fileType) ? fileType : [fileType]), [fileType]);

  const [index, setIndex] = useState(initialIndex || 0);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const currentFile = files[index];
  const currentType = types[index] ?? "image";
  const isMultiple = files.length > 1;

  // Reset view
  const resetView = useCallback(() => {
    setZoom(1);
    setRotation(0);
  }, []);

  const goNext = useCallback(() => {
    setIndex((i) => (i === files.length - 1 ? 0 : i + 1));
  }, [files.length]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i === 0 ? files.length - 1 : i - 1));
  }, [files.length]);

  useEffect(() => {
    if (open) {
      setIndex(initialIndex);
      resetView();
      setIsFullscreen(false);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open, initialIndex, resetView]);

  useEffect(() => {
    resetView();
  }, [index, resetView]);

  // keyboard controls
  useEffect(() => {
    if (!open) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsFullscreen(false);
      if (!isMultiple) return;

      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, isMultiple, goNext, goPrev]);

  // Zoom
  const zoomIn = () => setZoom((z) => Math.min(z + 0.25, 4));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.25, 0.25));

  // Download
  const handleDownload = useCallback(() => {
    if (!currentFile) return;

    const isExternal = currentFile.startsWith("http");

    if (isExternal) {
      window.open(currentFile, "_blank", "noopener,noreferrer");
      return;
    } else {
      const a = document.createElement("a");
      a.href = currentFile;
      a.download = names[index] || "file";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }, [currentFile, names, index]);

  const handleClose = () => {
    setIsFullscreen(false);
    onClose();
  };

  if (!currentFile) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isFullscreen}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            overflow: "hidden",
            width: isFullscreen ? "100vw" : "40vw",
            height: isFullscreen ? "100vh" : "60vh",
            maxWidth: "95vw",
            maxHeight: "95vh",
          },
        }
      }}
    >
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-300">
        {/* File name */}
        <span className="text-lg font-medium truncate">
          {names[index]}
        </span>

        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent className="flex flex-col bg-gray-100 overflow-hidden h-full" sx={{ p: 0 }}>
        <div className="relative flex items-center justify-center flex-1 overflow-hidden">
          {/* IMAGE */}
          {currentType === "image" && (
            <img
              src={currentFile}
              alt="preview"
              style={{
                transform: `scale(${zoom}) rotate(${rotation}deg)`,
                transition: "transform 0.2s ease",
                maxWidth: "90%",
                maxHeight: "90%",
                objectFit: "contain",
              }}
            />
          )}

          {/* VIDEO */}
          {currentType === "video" && (
            <video
              key={currentFile}
              src={currentFile}
              controls
              style={{
                maxWidth: "90%",
                maxHeight: "90%",
                objectFit: "contain"
              }}
            />
          )}

          {/* LEFT ARROW */}
          {isMultiple && (
            <IconButton
              onClick={goPrev}
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2000,
                background: "rgba(0,0,0,0.5)",
                color: "white",
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}

          {/* RIGHT ARROW */}
          {isMultiple && (
            <IconButton
              onClick={goNext}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2000,
                background: "rgba(0,0,0,0.5)",
                color: "white",
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          )}
        </div>

        {/* COUNTER */}
        {isMultiple && (
          <div className="text-center py-1 bg-gray-100">
            {index + 1} / {files.length}
          </div>
        )}

        {/* Toolbar */}
        <div className="flex justify-center py-3">
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl shadow bg-white">

            {currentType === "image" && (
              <>
                <Tooltip title="Zoom Out">
                  <IconButton onClick={zoomOut}>
                    <ZoomOutIcon />
                  </IconButton>
                </Tooltip>

                <span className="text-gray-500 text-sm min-w-[50px] text-center">
                  {Math.round(zoom * 100)}%
                </span>

                <Tooltip title="Zoom In">
                  <IconButton onClick={zoomIn}>
                    <ZoomInIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Rotate Left">
                  <IconButton onClick={() => setRotation(r => r - 90)}>
                    <RotateLeftIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Rotate Right">
                  <IconButton onClick={() => setRotation(r => r + 90)}>
                    <RotateRightIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Reset">
                  <IconButton onClick={resetView}>
                    <RestartAltIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}

            <Tooltip title="Download">
              <IconButton onClick={handleDownload}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
              <IconButton onClick={() => setIsFullscreen((f) => !f)}>
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>

          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PreviewDialog;